import { useEffect, useMemo } from 'react';
import Select from '@Common/Select';
import { Product } from '@Models/types';
import { useAuth } from '@Context/CognitoAuthProvider';
import Tabs from './Tabs';
import { useAnalyzerContext } from '@Context/AnalyzerContext';
import { ahaAPI } from '@Lib/axios/aha';
import FilterPods from '@Components/analyzer/controls/FilterPods';
import { Tooltip } from 'react-tooltip';
import { Warning } from '@phosphor-icons/react';
import colors from '@Common/assets/colors';

const warningColorMap: Record<number, string> = {
  3: '#FF7F50',
  4: '#FF6347',
  5: '#FF0000',
};

interface AhaPodsProps {
  onModeSwitch?: () => void;
  disabled: boolean;
}

const AhaPods = ({ onModeSwitch, disabled }: AhaPodsProps) => {
  const { user, logout } = useAuth();
  const aha = useMemo(() => ahaAPI(logout), [logout]);
  const {
    products,
    fetchProducts,
    selectedProduct,
    setSelectedProduct,
    selectedProducts,
    setSelectedProducts,
    loadingProd,
    setLoadingProd,
    selectedDateFilter,
    setSelectedDateFilter,
    filterStartDate,
    setFilterStartDate,
    filterEndDate,
    setFilterEndDate,
    showFilter,
    setShowFilter,
    globalLoading,
    removeSelectedProduct,
  } = useAnalyzerContext();

  const loading = loadingProd;

  useEffect(() => {
    // Only fetch if the user is authenticated and the list is empty
    if (user?.profile && products.length === 0) {
      setLoadingProd(true);
      fetchProducts().finally(() => setLoadingProd(false));
    }
  }, [user, products.length, setLoadingProd]);

  const productOptions = useMemo(
    () =>
      products.map((prod) => ({
        label: `${prod.name} (${prod.reference_prefix})`,
        value: prod,
      })),
    [products]
  );

  // 2. set selected projects (up to five)
  const handleProductChange = (
    selectedOption: { label: string; value: Product } | null
  ) => {
    if (selectedOption) {
      if (selectedProducts.length >= 5) {
        console.warn('Maximum 5 selections allowed');
        return;
      }
      const product = selectedOption.value;
      if (
        !selectedProducts.find(
          (p) => p.reference_prefix === product.reference_prefix
        )
      ) {
        setSelectedProducts([...selectedProducts, product]);
      }
      setSelectedProduct(null);
    }
  };

  const placeholder =
    loading && !selectedProduct
      ? 'Loading Aha Pods...'
      : selectedProduct
        ? `${selectedProduct.name} (${selectedProduct.reference_prefix})`
        : 'Select Aha Pod';

  // define favorites
  const favorites = useMemo(
    () => ({
      enabled: true,
      type: 'product' as const,
      fetch: aha.getFavProd,
      save: aha.saveFavProd,
      favoriteKey: 'reference_prefix' as const,
    }),
    [aha]
  );

  return (
    <div data-testid="test-products" className="rounded-lg bg-white p-0">
      {/* Main Container: Left Col (Tabs) + Right Col (Everything Else) */}
      <div className="flex w-full items-start gap-x-3 pl-1">
        {/* LEFT COLUMN: Tabs */}
        {/* pt-1 or mt-1 might be needed here to perfectly center-align the tab text with the Select input text, depending on your Select component's height */}
        <div className="shrink-0 pt-1">
          <Tabs
            onModeSwitch={onModeSwitch}
            isJiraActive={false}
            disabled={disabled}
          />
        </div>

        {/* RIGHT COLUMN: Controls (Top) + Selected Items (Bottom) */}
        <div className="flex flex-1 flex-col gap-y-2">
          {/* Right Col - Top Row: Select + Counter + Filter */}
          <div className="flex w-full items-center gap-x-2">
            <div className="w-[400px] shrink-0">
              <Select
                type="product"
                options={productOptions}
                onChange={handleProductChange}
                placeholder={placeholder}
                loading={loading}
                disabled={loading || disabled}
                favorites={favorites}
              />
            </div>

            <div className="flex shrink-0 items-center gap-x-2">
              {selectedProducts.length <= 5 && (
                <div className="flex items-center whitespace-nowrap rounded bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600">
                  {selectedProducts.length}/5
                </div>
              )}

              <FilterPods
                selectedDateFilter={selectedDateFilter}
                setSelectedDateFilter={setSelectedDateFilter}
                filterStartDate={filterStartDate}
                setFilterStartDate={setFilterStartDate}
                filterEndDate={filterEndDate}
                setFilterEndDate={setFilterEndDate}
                setShowFilter={setShowFilter}
                showFilter={showFilter}
                disabled={globalLoading}
              />

              {selectedProducts.length >= 3 && selectedProducts.length <= 5 && (
                <span
                  data-tooltip-id="pod-warning"
                  data-tooltip-content={`Activating ${selectedProducts.length} pods may take a while to process.\nPerformance may be affected.\nSome tickets may not be classified properly.`}
                  data-tooltip-place="top"
                  className="cursor-help"
                >
                  <Warning
                    size={24}
                    color={warningColorMap[selectedProducts.length]}
                    weight="fill"
                  />
                </span>
              )}
            </div>
          </div>

          {/* Right Col - Bottom Row: Selected Pod Tags */}
          {/* Because this is inside the Right Column, it will never cross under the Tabs */}
          {selectedProducts.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedProducts.map((prod) => (
                <div
                  key={prod.reference_prefix}
                  className="flex items-center gap-x-1.5 rounded bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-700"
                >
                  {prod.name}
                  <button
                    onClick={() =>
                      removeSelectedProduct(prod.reference_prefix || '')
                    }
                    className="font-bold text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Tooltip
        id="pod-warning"
        style={{
          color: 'white',
          background: colors.primary[700],
          borderRadius: '0.5rem',
        }}
        className="font-medium"
      />
    </div>
  );
};

export default AhaPods;
